/**
 * AddEditServiceForm.tsx
 *
 * Architecture:
 *  ┌─ AddEditServiceForm (data shell) ──────────────────────────────────┐
 *  │  Fetches serviceDetail + staffList. Shows a spinner until ready.   │
 *  │  Renders ServiceFormInner with a key={serviceDetail.id} so that    │
 *  │  the form always MOUNTS with the correct defaultValues.            │
 *  └────────────────────────────────────────────────────────────────────┘
 *
 *  ┌─ ServiceFormInner (pure form) ─────────────────────────────────────┐
 *  │  Receives pre-loaded data as props. Calls useForm() ONCE with the  │
 *  │  correct defaultValues. No reset() calls needed — Radix Select     │
 *  │  gets the right value on its very first render, so dropdowns       │
 *  │  always display correctly.                                         │
 *  └────────────────────────────────────────────────────────────────────┘
 *
 * Layout mirrors the cloud AddFoodEntryForm pattern: single Card shell
 * with header + Tabs; each tab is an inner Card with label-left field rows
 * (FormItem > flex items-center gap-4 > FormLabel w-[170px] + control flex-1).
 * View and Edit pages line up visually because ServiceDetailView uses the
 * same shell with ReadField rows.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ArrowLeft, Upload, Loader2, ImageOff, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { useDirtyFlag } from "@/hooks/useUnsavedChanges";
import {
  useCreateService,
  useUpdateService,
  useServiceDetail,
  useStaffForService,
} from "@/hooks/useManageServices";
import { useClinics } from "@/hooks/useClinics";
import { useUnsavedChangesStore } from "@/stores/unsavedChangesStore";
import type {
  ServiceCategory,
  ServiceDetail,
  ServiceStaffMember,
} from "@/types/services";
import type { Clinic } from "@/types/clinics";

// ─── Schema ──────────────────────────────────────────────────────────────────

const requiredText = (label: string) =>
  z.string().trim().min(1, `${label} is required`);

const numericText = (label: string, { allowZero }: { allowZero: boolean }) =>
  requiredText(label)
    .refine((value) => Number.isFinite(Number(value)), {
      message: `${label} must be a valid number`,
    })
    .refine((value) => (allowZero ? Number(value) >= 0 : Number(value) > 0), {
      message: allowZero
        ? `${label} cannot be negative`
        : `${label} must be greater than 0`,
    });

const integerText = (label: string, { allowZero }: { allowZero: boolean }) =>
  numericText(label, { allowZero }).refine(
    (value) => Number.isInteger(Number(value)),
    { message: `${label} must be a whole number` }
  );

const optionalNumericText = (label: string, allowZero: boolean) =>
  z
    .string()
    .optional()
    .refine(
      (value) =>
        value === undefined || value === "" || Number.isFinite(Number(value)),
      { message: `${label} must be a valid number` }
    )
    .refine(
      (value) =>
        value === undefined ||
        value === "" ||
        (allowZero ? Number(value) >= 0 : Number(value) > 0),
      {
        message: allowZero
          ? `${label} cannot be negative`
          : `${label} must be greater than 0`,
      }
    );

const serviceSchema = z
  .object({
    name: requiredText("Service name"),
    category_id: requiredText("Department"),
    description: requiredText("Description"),
    type: requiredText("Service type"),
    event_duration: integerText("Service duration", { allowZero: false }),
    slot_duration: integerText("Slot duration", { allowZero: false }),
    start_buffer: integerText("Start buffer", { allowZero: true }),
    end_buffer: integerText("End buffer", { allowZero: true }),
    min_time_to_start: integerText("Min time to start", { allowZero: true }),
    max_participants: numericText("Maximum participants", { allowZero: false }),
    cost: numericText("Amount", { allowZero: true }),
    location: requiredText("Location"),
    mode_of_service: requiredText("Mode of consultation"),
    is_active: z.boolean(),
    visible: z.boolean(),
    order: numericText("Order", { allowZero: true }),
    is_followup_available: z.boolean(),
    followup_limit_days: optionalNumericText("Follow-up limit days", true),
    followup_event_duration: optionalNumericText("Follow-up duration", true),
    followup_description: z.string().optional(),
    followup_cost: optionalNumericText("Follow-up cost", true),
    is_in_person_available: z.boolean().default(false),
    available_clinic_ids: z.array(z.string()).default([]),
  })
  .superRefine((values, ctx) => {
    if (values.is_followup_available) {
      const followupRequiredFields = [
        { name: "followup_limit_days", label: "Follow-up limit days" },
        { name: "followup_event_duration", label: "Follow-up duration" },
        { name: "followup_cost", label: "Follow-up cost" },
      ] as const;

      followupRequiredFields.forEach(({ name, label }) => {
        if (!String(values[name] ?? "").trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `${label} is required when follow-up is available`,
            path: [name],
          });
          return;
        }

        if (name !== "followup_cost" && Number(values[name]) <= 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `${label} must be greater than 0`,
            path: [name],
          });
        }
      });
    }

    if (
      values.is_in_person_available &&
      values.available_clinic_ids.length === 0
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Select at least one clinic when in-person is available",
        path: ["available_clinic_ids"],
      });
    }
  });

type ServiceFormValues = z.infer<typeof serviceSchema>;

const RequiredMark = () => (
  <span className="ml-0.5 text-destructive" aria-hidden="true">
    *
  </span>
);

const sameStringSet = (a: string[], b: string[]) => {
  if (a.length !== b.length) return false;
  const left = [...a].sort();
  const right = [...b].sort();
  return left.every((value, index) => value === right[index]);
};

// ─── Constants ────────────────────────────────────────────────────────────────

const PARTICIPANT_OPTIONS = Array.from({ length: 10 }, (_, i) => ({
  label: String(i + 1),
  value: String(i + 1),
}));

const LOCATION_OPTIONS = [
  { label: "📍 Tele-Consultation", value: "Tele-Consultation" },
  { label: "📍 Remote", value: "Remote" },
];

const MODE_OPTIONS = [
  { label: "Audio", value: "audio" },
  { label: "Video", value: "video" },
];

const SERVICE_TYPE_OPTIONS = [
  { label: "Baseline", value: "baseline" },
  { label: "Regular", value: "regular" },
  { label: "Urgent", value: "urgent" },
  { label: "Other", value: "other" },
];

const LABEL_WIDTH = "170px";

// ─── Helper: map ServiceDetail → form values ─────────────────────────────────

function buildDefaultValues(detail?: ServiceDetail): ServiceFormValues {
  if (!detail) {
    return {
      name: "",
      category_id: "",
      description: "",
      type: "other",
      event_duration: "",
      slot_duration: "",
      start_buffer: "0",
      end_buffer: "0",
      min_time_to_start: "0",
      max_participants: "",
      cost: "",
      location: "",
      mode_of_service: "",
      is_active: true,
      visible: true,
      order: "0",
      is_followup_available: false,
      followup_limit_days: "0",
      followup_event_duration: "0",
      followup_description: "",
      followup_cost: "0.00",
      is_in_person_available: false,
      available_clinic_ids: [],
    };
  }

  const modeValue = detail.mode ?? detail.mode_of_service ?? "";
  const locationValue = detail.location ?? "";

  return {
    name: detail.name ?? "",
    category_id: detail.category_id ?? "",
    description: detail.description ?? "",
    type: detail.type ?? "other",
    event_duration: String(detail.event_duration ?? ""),
    slot_duration: String(detail.slot_duration ?? ""),
    start_buffer: String(detail.start_buffer ?? 0),
    end_buffer: String(detail.end_buffer ?? 0),
    min_time_to_start: String(detail.min_time_to_start ?? 0),
    max_participants: String(detail.max_participants ?? ""),
    cost: detail.cost ?? "",
    location: locationValue,
    mode_of_service: modeValue,
    is_active: detail.is_active ?? true,
    visible: detail.visible ?? true,
    order: String(detail.order ?? 0),
    is_followup_available: detail.is_followup_available ?? false,
    followup_limit_days: String(detail.followup_limit_days ?? 0),
    followup_event_duration: String(detail.followup_event_duration ?? 0),
    followup_description: detail.followup_description ?? "",
    followup_cost: detail.followup_cost ?? "0.00",
    is_in_person_available: detail.is_in_person_available ?? false,
    available_clinic_ids: detail.available_clinic_ids ?? [],
  };
}

// ─── Staff picker (Assigned / Unassigned sub-tabs) ───────────────────────────

interface StaffPickerProps {
  staffList: ServiceStaffMember[];
  selectedStaffIds: string[];
  onToggle: (id: string) => void;
  isStaffDirty: boolean;
}

const StaffPicker = ({
  staffList,
  selectedStaffIds,
  onToggle,
  isStaffDirty,
}: StaffPickerProps) => {
  const [tab, setTab] = useState<"assigned" | "unassigned">("assigned");

  const assigned = useMemo(
    () => staffList.filter((s) => selectedStaffIds.includes(s.id)),
    [staffList, selectedStaffIds]
  );
  const unassigned = useMemo(
    () => staffList.filter((s) => !selectedStaffIds.includes(s.id)),
    [staffList, selectedStaffIds]
  );

  const renderRows = (rows: ServiceStaffMember[], emptyText: string) => {
    if (rows.length === 0) {
      return (
        <p className="z-caption text-muted-foreground py-6 text-center">
          {emptyText}
        </p>
      );
    }
    return (
      <div className="divide-y divide-border max-h-64 overflow-y-auto scrollbar-thin">
        {rows.map((staff) => {
          const isSelected = selectedStaffIds.includes(staff.id);
          return (
            <div
              key={staff.id}
              role="option"
              aria-selected={isSelected}
              className={cn(
                "grid items-center px-4 py-3 transition-colors cursor-pointer select-none",
                "hover:bg-muted/50",
                isSelected && "bg-primary/5"
              )}
              style={{ gridTemplateColumns: "40px 1fr 1fr" }}
              onClick={() => onToggle(staff.id)}
            >
              {/*
               * Pure CSS checkbox — NOT shadcn Checkbox (Radix UI).
               * Radix's Checkbox calls setRef internally on checked change → infinite loop.
               */}
              <div
                aria-hidden="true"
                className={cn(
                  "h-4 w-4 rounded border-2 flex items-center justify-center shrink-0 transition-colors",
                  isSelected
                    ? "border-primary bg-primary"
                    : "border-border bg-background"
                )}
              >
                {isSelected && (
                  <Check
                    className="h-2.5 w-2.5 text-primary-foreground"
                    strokeWidth={3}
                  />
                )}
              </div>

              <div className="flex items-center gap-2 min-w-0">
                <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center z-caption font-medium text-foreground overflow-hidden shrink-0">
                  {staff.profile_pic ? (
                    <img
                      src={staff.profile_pic}
                      alt=""
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = "none";
                      }}
                    />
                  ) : (
                    `${staff.first_name?.[0] ?? ""}${staff.last_name?.[0] ?? ""}`
                  )}
                </div>
                <span className="z-body font-medium truncate">
                  {staff.first_name} {staff.last_name}
                </span>
              </div>

              <Badge
                variant="outline"
                className="w-fit z-caption text-muted-foreground capitalize"
              >
                {staff.user_type?.replace(/_/g, " ") ?? "Staff"}
              </Badge>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)}>
      <TabsList>
        <TabsTrigger value="assigned">
          Assigned
          <Badge variant="secondary" className="ml-2 z-hint">
            {assigned.length}
          </Badge>
        </TabsTrigger>
        <TabsTrigger value="unassigned">
          Unassigned
          <Badge variant="secondary" className="ml-2 z-hint">
            {unassigned.length}
          </Badge>
        </TabsTrigger>
      </TabsList>

      <TabsContent value="assigned" className="mt-3">
        <div
          className={cn(
            "rounded-lg border border-border overflow-hidden",
            isStaffDirty && "border-warning"
          )}
        >
          <div
            className="grid items-center px-4 py-2.5 bg-muted/60 border-b border-border z-eyebrow text-muted-foreground"
            style={{ gridTemplateColumns: "40px 1fr 1fr" }}
          >
            <span />
            <span>Name</span>
            <span>Role</span>
          </div>
          {renderRows(
            assigned,
            "No staff assigned yet. Switch to Unassigned to add staff."
          )}
        </div>
      </TabsContent>

      <TabsContent value="unassigned" className="mt-3">
        <div
          className={cn(
            "rounded-lg border border-border overflow-hidden",
            isStaffDirty && "border-warning"
          )}
        >
          <div
            className="grid items-center px-4 py-2.5 bg-muted/60 border-b border-border z-eyebrow text-muted-foreground"
            style={{ gridTemplateColumns: "40px 1fr 1fr" }}
          >
            <span />
            <span>Name</span>
            <span>Role</span>
          </div>
          {renderRows(unassigned, "All staff members are assigned.")}
        </div>
      </TabsContent>
    </Tabs>
  );
};

// ─── Clinic picker (checkbox grid) ───────────────────────────────────────────

interface ClinicPickerProps {
  clinics: Clinic[];
  selectedClinicIds: string[];
  onToggle: (id: string) => void;
  isDirty: boolean;
}

const ClinicPicker = ({
  clinics,
  selectedClinicIds,
  onToggle,
  isDirty,
}: ClinicPickerProps) => {
  return (
    <div
      className={cn(
        "grid grid-cols-1 sm:grid-cols-2 gap-1.5 rounded-lg border border-border p-2 max-h-64 overflow-y-auto scrollbar-thin",
        isDirty && "border-warning"
      )}
    >
      {clinics.map((clinic) => {
        const isSelected = selectedClinicIds.includes(clinic.id);
        return (
          <div
            key={clinic.id}
            role="option"
            aria-selected={isSelected}
            className={cn(
              "flex items-center gap-2 rounded-md px-3 py-2 transition-colors cursor-pointer select-none",
              "hover:bg-muted/50",
              isSelected && "bg-primary/5"
            )}
            onClick={() => onToggle(clinic.id)}
          >
            {/*
             * Pure CSS checkbox — NOT shadcn Checkbox (Radix UI).
             * Radix's Checkbox calls setRef internally on checked change → infinite loop.
             */}
            <div
              aria-hidden="true"
              className={cn(
                "h-4 w-4 rounded border-2 flex items-center justify-center shrink-0 transition-colors",
                isSelected
                  ? "border-primary bg-primary"
                  : "border-border bg-background"
              )}
            >
              {isSelected && (
                <Check
                  className="h-2.5 w-2.5 text-primary-foreground"
                  strokeWidth={3}
                />
              )}
            </div>
            <span className="z-body font-medium truncate">{clinic.name}</span>
          </div>
        );
      })}
    </div>
  );
};

// ─── Inner form (pure) ───────────────────────────────────────────────────────

interface ServiceFormInnerProps {
  serviceId: string;
  isEditMode: boolean;
  categories: ServiceCategory[];
  staffList: ServiceStaffMember[];
  isLoadingStaff: boolean;
  initialStaffIds: string[];
  initialImageUrl: string;
  initialName: string;
  defaultValues: ServiceFormValues;
  onBack: () => void;
}

const ServiceFormInner = ({
  serviceId,
  isEditMode,
  categories,
  staffList,
  isLoadingStaff,
  initialStaffIds,
  initialImageUrl,
  initialName,
  defaultValues,
  onBack,
}: ServiceFormInnerProps) => {
  const { toast } = useToast();

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>("");
  const [existingImageUrl, setExistingImageUrl] =
    useState<string>(initialImageUrl);
  const [imageError, setImageError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedStaffIds, setSelectedStaffIds] =
    useState<string[]>(initialStaffIds);
  const [staffError, setStaffError] = useState("");

  const { data: clinics = [] } = useClinics();
  const [availableClinicIds, setAvailableClinicIds] = useState<string[]>(
    defaultValues.available_clinic_ids ?? []
  );

  const createMutation = useCreateService();
  const updateMutation = useUpdateService();

  const form = useForm<ServiceFormValues>({
    resolver: zodResolver(serviceSchema),
    defaultValues,
    mode: "onChange",
  });
  const { trigger } = form;
  const {
    dirtyFields,
    isDirty: isFormDirty,
    isValid,
    isValidating,
  } = form.formState;

  useEffect(() => {
    if (!isEditMode) return;
    void trigger(undefined, { shouldFocus: false });
  }, [isEditMode, trigger]);

  const isFollowupAvailable = form.watch("is_followup_available");
  const isInPersonAvailable = form.watch("is_in_person_available");

  // Keep the RHF-managed `available_clinic_ids` field in sync with the
  // checkbox-grid state so zod's superRefine can validate it (mirrors the
  // selectedStaffIds pattern, but this field also participates in schema
  // validation, so it needs to live in the form too).
  useEffect(() => {
    form.setValue("available_clinic_ids", availableClinicIds, {
      shouldValidate: true,
      shouldDirty: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableClinicIds]);

  const dirtyKey = `manage-service:${isEditMode ? serviceId : "new"}`;
  const clearDirty = useUnsavedChangesStore((s) => s.clearKey);
  const isStaffDirty = useMemo(
    () => !sameStringSet(selectedStaffIds, initialStaffIds),
    [selectedStaffIds, initialStaffIds]
  );
  const isDirty = isFormDirty || isStaffDirty || Boolean(imageFile);
  useDirtyFlag(dirtyKey, isDirty, isEditMode ? "Edit Service" : "Add Service");

  const isFieldDirty = useCallback(
    (name: keyof ServiceFormValues) => Boolean(dirtyFields[name]),
    [dirtyFields]
  );

  const imageDirty = Boolean(imageFile);

  // ── Image handling ──────────────────────────────────────────────────────────

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1048576) {
      toast({
        title: "Image too large",
        description: "Maximum image size is 1 MB.",
        variant: "destructive",
      });
      return;
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setExistingImageUrl("");
    setImageError("");
  };

  const handleStaffToggle = useCallback((staffId: string) => {
    setStaffError("");
    setSelectedStaffIds((prev) =>
      prev.includes(staffId)
        ? prev.filter((id) => id !== staffId)
        : [...prev, staffId]
    );
  }, []);

  const handleClinicToggle = useCallback((clinicId: string) => {
    setAvailableClinicIds((prev) =>
      prev.includes(clinicId)
        ? prev.filter((id) => id !== clinicId)
        : [...prev, clinicId]
    );
  }, []);

  // ── FormData builder ────────────────────────────────────────────────────────

  const buildFormData = (values: ServiceFormValues): FormData => {
    const fd = new FormData();
    fd.append("name", values.name);
    fd.append("category_id", values.category_id);
    fd.append("description", values.description);
    fd.append("type", values.type);
    fd.append("event_duration", values.event_duration);
    fd.append("slot_duration", values.slot_duration);
    fd.append("start_buffer", values.start_buffer);
    fd.append("end_buffer", values.end_buffer);
    fd.append("min_time_to_start", values.min_time_to_start);
    fd.append("max_participants", values.max_participants);
    fd.append("cost", values.cost);
    fd.append("location", values.location);
    // POST/PUT field name is mode_of_service (different from the read-side "mode")
    fd.append("mode_of_service", values.mode_of_service);
    fd.append("is_active", String(values.is_active));
    fd.append("visible", String(values.visible));
    fd.append("order", values.order);
    fd.append("is_followup_available", String(values.is_followup_available));
    fd.append("followup_limit_days", values.followup_limit_days ?? "0");
    fd.append("followup_event_duration", values.followup_event_duration ?? "0");
    fd.append("followup_description", values.followup_description ?? "");
    fd.append("followup_cost", values.followup_cost ?? "0.00");
    fd.append("is_in_person_available", String(values.is_in_person_available));
    fd.append("available_clinic_ids", JSON.stringify(availableClinicIds));
    fd.append("staff_ids", JSON.stringify(selectedStaffIds));
    if (imageFile) {
      fd.append("image", imageFile, imageFile.name);
    }
    return fd;
  };

  // ── Submit ──────────────────────────────────────────────────────────────────

  const onSubmit = async (values: ServiceFormValues) => {
    if (!imageFile && !existingImageUrl) {
      setImageError("Service image is required.");
      toast({
        title: "Image required",
        description: "Please select a service image.",
        variant: "destructive",
      });
      return;
    }
    if (!isEditMode && selectedStaffIds.length === 0) {
      setStaffError("Select at least one staff member for this service.");
      toast({
        title: "Staff required",
        description: "Select at least one staff member before saving.",
        variant: "destructive",
      });
      return;
    }

    const formData = buildFormData(values);

    try {
      if (isEditMode) {
        await updateMutation.mutateAsync({ serviceId, formData });
        toast({ title: "Service updated successfully." });
      } else {
        await createMutation.mutateAsync(formData);
        toast({ title: "Service added successfully." });
      }
      clearDirty(dirtyKey);
      onBack();
    } catch {
      toast({
        title: "Something went wrong",
        description: "Please check your inputs and try again.",
        variant: "destructive",
      });
    }
  };

  const isMutating = createMutation.isPending || updateMutation.isPending;
  const displayImage = imagePreview || existingImageUrl;
  const isSaveDisabled = isMutating || isValidating || !isDirty || !isValid;

  // ── Helpers for rendering label-left rows ───────────────────────────────────

  const labelClass = (name: keyof ServiceFormValues) =>
    cn(
      "z-label shrink-0 mb-0",
      isFieldDirty(name) && "text-warning font-semibold"
    );

  const inputClass = (name: keyof ServiceFormValues) =>
    cn("flex-1", isFieldDirty(name) && "border-warning border-2");

  const selectClass = (name: keyof ServiceFormValues) =>
    cn("flex-1 max-w-[400px]", isFieldDirty(name) && "border-warning border-2");

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="h-full flex flex-col min-h-0 gap-4">
      <Card className="max-w-[2000px] flex-1 flex flex-col min-h-0 overflow-visible">
        <CardHeader className="shrink-0">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1 flex items-center gap-3">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={onBack}
                aria-label="Back to Services"
                className="shrink-0"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div className="flex items-center gap-3 flex-wrap min-w-0">
                <CardTitle className="z-h2 truncate">
                  {isEditMode ? initialName : "Add Service"}
                </CardTitle>
                {isDirty && <Badge variant="warning">Editing</Badge>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                id="cancel-service-btn"
                type="button"
                variant="outline"
                onClick={onBack}
                disabled={isMutating}
              >
                Cancel
              </Button>
              <Button
                id="save-service-btn"
                type="submit"
                form="service-form"
                disabled={isSaveDisabled}
                className="min-w-24"
              >
                {isMutating ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : isEditMode ? (
                  "Update Service"
                ) : (
                  "Save Service"
                )}
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="flex-1 overflow-y-auto pt-2 pb-6">
          <Form {...form}>
            <form
              id="service-form"
              onSubmit={form.handleSubmit(onSubmit)}
              className="space-y-4"
            >
              <Tabs defaultValue="details">
                <TabsList>
                  <TabsTrigger value="details">Details</TabsTrigger>
                  <TabsTrigger value="schedule">Schedule</TabsTrigger>
                  <TabsTrigger value="pricing">
                    Pricing &amp; Follow-up
                  </TabsTrigger>
                  <TabsTrigger value="staff">
                    Staff &amp; Visibility
                  </TabsTrigger>
                </TabsList>

                {/* ── Details ───────────────────────────────────────────── */}
                <TabsContent value="details" className="mt-4">
                  <Card>
                    <CardContent className="pt-6 pb-5 space-y-5">
                      {/* Image */}
                      <div className="flex items-start gap-4">
                        <span
                          className="z-label shrink-0 mb-0 text-muted-foreground pt-1"
                          style={{ width: LABEL_WIDTH }}
                        >
                          Service Image
                          <RequiredMark />
                        </span>
                        <div className="flex-1 flex flex-col gap-2 max-w-[400px]">
                          <div
                            id="service-image-upload"
                            onClick={() => fileInputRef.current?.click()}
                            className={cn(
                              "w-40 h-40 rounded-lg border-2 border-dashed border-input cursor-pointer",
                              "transition-colors hover:border-primary/50 hover:bg-muted/40",
                              "flex flex-col items-center justify-center gap-2 overflow-hidden bg-muted/30",
                              imageDirty && "border-warning bg-warning/10"
                            )}
                          >
                            {displayImage ? (
                              <img
                                src={displayImage}
                                alt="Service preview"
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display =
                                    "none";
                                }}
                              />
                            ) : (
                              <>
                                <Upload className="h-5 w-5 text-muted-foreground" />
                                <div className="text-center px-2">
                                  <p className="z-caption font-medium text-primary">
                                    Click to upload
                                  </p>
                                  <p className="z-hint text-muted-foreground">
                                    PNG, JPG (max 1 MB)
                                  </p>
                                </div>
                              </>
                            )}
                          </div>
                          <input
                            type="file"
                            ref={fileInputRef}
                            accept="image/png,image/jpeg"
                            className="hidden"
                            onChange={handleImageChange}
                          />
                          {displayImage && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="w-fit z-caption text-muted-foreground"
                              onClick={() => fileInputRef.current?.click()}
                            >
                              <ImageOff className="h-3 w-3 mr-1" />
                              Change image
                            </Button>
                          )}
                          {imageError && (
                            <p className="z-caption font-medium text-destructive">
                              {imageError}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-4">
                        {/* Service Name */}
                        <FormField
                          control={form.control}
                          name="name"
                          render={({ field }) => (
                            <FormItem>
                              <div className="flex items-center gap-4">
                                <FormLabel
                                  className={labelClass("name")}
                                  style={{ width: LABEL_WIDTH }}
                                >
                                  Service Name
                                  <RequiredMark />
                                </FormLabel>
                                <FormControl>
                                  <Input
                                    id="service-name-input"
                                    placeholder="Enter a name for the service"
                                    className={inputClass("name")}
                                    {...field}
                                  />
                                </FormControl>
                              </div>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {/* Department */}
                        <FormField
                          control={form.control}
                          name="category_id"
                          render={({ field }) => (
                            <FormItem>
                              <div className="flex items-center gap-4">
                                <FormLabel
                                  className={labelClass("category_id")}
                                  style={{ width: LABEL_WIDTH }}
                                >
                                  Department
                                  <RequiredMark />
                                </FormLabel>
                                <Select
                                  value={field.value}
                                  onValueChange={field.onChange}
                                >
                                  <FormControl>
                                    <SelectTrigger
                                      id="service-department-select"
                                      className={selectClass("category_id")}
                                    >
                                      <SelectValue placeholder="Select category" />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    {categories.map((cat) => (
                                      <SelectItem key={cat.id} value={cat.id}>
                                        {cat.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {/* Service Type */}
                        <FormField
                          control={form.control}
                          name="type"
                          render={({ field }) => (
                            <FormItem>
                              <div className="flex items-center gap-4">
                                <FormLabel
                                  className={labelClass("type")}
                                  style={{ width: LABEL_WIDTH }}
                                >
                                  Service Type
                                  <RequiredMark />
                                </FormLabel>
                                <Select
                                  value={field.value}
                                  onValueChange={field.onChange}
                                >
                                  <FormControl>
                                    <SelectTrigger
                                      id="service-type-select"
                                      className={selectClass("type")}
                                    >
                                      <SelectValue placeholder="Select type" />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    {SERVICE_TYPE_OPTIONS.map((opt) => (
                                      <SelectItem
                                        key={opt.value}
                                        value={opt.value}
                                      >
                                        {opt.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {/* Max Participants */}
                        <FormField
                          control={form.control}
                          name="max_participants"
                          render={({ field }) => (
                            <FormItem>
                              <div className="flex items-center gap-4">
                                <FormLabel
                                  className={labelClass("max_participants")}
                                  style={{ width: LABEL_WIDTH }}
                                >
                                  Max Participants
                                  <RequiredMark />
                                </FormLabel>
                                <Select
                                  value={field.value}
                                  onValueChange={field.onChange}
                                >
                                  <FormControl>
                                    <SelectTrigger
                                      id="max-participants-select"
                                      className={selectClass(
                                        "max_participants"
                                      )}
                                    >
                                      <SelectValue placeholder="Select participants" />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    {PARTICIPANT_OPTIONS.map((opt) => (
                                      <SelectItem
                                        key={opt.value}
                                        value={opt.value}
                                      >
                                        {opt.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {/* Location */}
                        <FormField
                          control={form.control}
                          name="location"
                          render={({ field }) => (
                            <FormItem>
                              <div className="flex items-center gap-4">
                                <FormLabel
                                  className={labelClass("location")}
                                  style={{ width: LABEL_WIDTH }}
                                >
                                  Location
                                  <RequiredMark />
                                </FormLabel>
                                <Select
                                  value={field.value}
                                  onValueChange={field.onChange}
                                >
                                  <FormControl>
                                    <SelectTrigger
                                      id="location-select"
                                      className={selectClass("location")}
                                    >
                                      <SelectValue placeholder="Select location" />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    {LOCATION_OPTIONS.map((opt) => (
                                      <SelectItem
                                        key={opt.value}
                                        value={opt.value}
                                      >
                                        {opt.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {/* Mode of Consultation */}
                        <FormField
                          control={form.control}
                          name="mode_of_service"
                          render={({ field }) => (
                            <FormItem>
                              <div className="flex items-center gap-4">
                                <FormLabel
                                  className={labelClass("mode_of_service")}
                                  style={{ width: LABEL_WIDTH }}
                                >
                                  Mode of Consultation
                                  <RequiredMark />
                                </FormLabel>
                                <Select
                                  value={field.value}
                                  onValueChange={field.onChange}
                                >
                                  <FormControl>
                                    <SelectTrigger
                                      id="mode-of-consultation-select"
                                      className={selectClass("mode_of_service")}
                                    >
                                      <SelectValue placeholder="Select mode" />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    {MODE_OPTIONS.map((opt) => (
                                      <SelectItem
                                        key={opt.value}
                                        value={opt.value}
                                      >
                                        {opt.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      {/* Available for In-Person — show when clinics exist, or when already enabled (so user can turn it off) */}
                      {(clinics.length > 0 || isInPersonAvailable) && (
                        <FormField
                          control={form.control}
                          name="is_in_person_available"
                          render={({ field }) => (
                            <FormItem>
                              <div className="flex items-center gap-4">
                                <FormLabel
                                  className={labelClass(
                                    "is_in_person_available"
                                  )}
                                  style={{ width: LABEL_WIDTH }}
                                >
                                  Available for In-Person
                                </FormLabel>
                                <FormControl>
                                  <Switch
                                    checked={field.value}
                                    onCheckedChange={(checked) => {
                                      field.onChange(checked);
                                      if (!checked) setAvailableClinicIds([]);
                                    }}
                                  />
                                </FormControl>
                              </div>
                            </FormItem>
                          )}
                        />
                      )}

                      {isInPersonAvailable && (
                        <div className="space-y-2">
                          <div className="flex items-center gap-4">
                            <span
                              className="z-label shrink-0 mb-0"
                              style={{ width: LABEL_WIDTH }}
                            >
                              Clinics
                              <RequiredMark />
                            </span>
                            {availableClinicIds.length > 0 && (
                              <Badge variant="secondary" className="z-caption">
                                {availableClinicIds.length} selected
                              </Badge>
                            )}
                          </div>
                          {clinics.length > 0 ? (
                            <ClinicPicker
                              clinics={clinics}
                              selectedClinicIds={availableClinicIds}
                              onToggle={handleClinicToggle}
                              isDirty={isFieldDirty("available_clinic_ids")}
                            />
                          ) : (
                            <p className="z-caption text-muted-foreground">
                              No clinics available. Turn off in-person or
                              contact your administrator.
                            </p>
                          )}
                          {form.formState.errors.available_clinic_ids && (
                            <p className="z-caption font-medium text-destructive">
                              {
                                form.formState.errors.available_clinic_ids
                                  .message
                              }
                            </p>
                          )}
                        </div>
                      )}

                      {/* Description — full width */}
                      <FormField
                        control={form.control}
                        name="description"
                        render={({ field }) => (
                          <FormItem>
                            <div className="flex items-start gap-4">
                              <FormLabel
                                className={cn(
                                  labelClass("description"),
                                  "pt-2"
                                )}
                                style={{ width: LABEL_WIDTH }}
                              >
                                Description
                                <RequiredMark />
                              </FormLabel>
                              <FormControl>
                                <Textarea
                                  id="service-description-input"
                                  placeholder="Please enter description"
                                  rows={3}
                                  className={cn(
                                    "flex-1 resize-none",
                                    isFieldDirty("description") &&
                                      "border-warning border-2"
                                  )}
                                  {...field}
                                />
                              </FormControl>
                            </div>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* ── Schedule ──────────────────────────────────────────── */}
                <TabsContent value="schedule" className="mt-4">
                  <Card>
                    <CardContent className="pt-6 pb-5 space-y-4">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-4">
                        {/* Event Duration */}
                        <FormField
                          control={form.control}
                          name="event_duration"
                          render={({ field }) => (
                            <FormItem>
                              <div className="flex items-center gap-4">
                                <FormLabel
                                  className={labelClass("event_duration")}
                                  style={{ width: LABEL_WIDTH }}
                                >
                                  Service Duration
                                  <RequiredMark />
                                </FormLabel>
                                <FormControl>
                                  <Input
                                    id="service-duration-input"
                                    type="number"
                                    inputMode="numeric"
                                    min={1}
                                    step={1}
                                    placeholder="Enter duration in minutes"
                                    className={inputClass("event_duration")}
                                    {...field}
                                  />
                                </FormControl>
                              </div>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {/* Slot Duration */}
                        <FormField
                          control={form.control}
                          name="slot_duration"
                          render={({ field }) => (
                            <FormItem>
                              <div className="flex items-center gap-4">
                                <FormLabel
                                  className={labelClass("slot_duration")}
                                  style={{ width: LABEL_WIDTH }}
                                >
                                  Slot Duration
                                  <RequiredMark />
                                </FormLabel>
                                <FormControl>
                                  <Input
                                    id="slot-duration-input"
                                    type="number"
                                    inputMode="numeric"
                                    min={1}
                                    step={1}
                                    placeholder="Enter slot duration in minutes"
                                    className={inputClass("slot_duration")}
                                    {...field}
                                  />
                                </FormControl>
                              </div>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {/* Start Buffer */}
                        <FormField
                          control={form.control}
                          name="start_buffer"
                          render={({ field }) => (
                            <FormItem>
                              <div className="flex items-center gap-4">
                                <FormLabel
                                  className={labelClass("start_buffer")}
                                  style={{ width: LABEL_WIDTH }}
                                >
                                  Start Buffer
                                  <RequiredMark />
                                </FormLabel>
                                <FormControl>
                                  <Input
                                    id="start-buffer-time-input"
                                    type="number"
                                    inputMode="numeric"
                                    min={0}
                                    step={1}
                                    placeholder="Enter start buffer in minutes"
                                    className={inputClass("start_buffer")}
                                    {...field}
                                  />
                                </FormControl>
                              </div>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {/* End Buffer */}
                        <FormField
                          control={form.control}
                          name="end_buffer"
                          render={({ field }) => (
                            <FormItem>
                              <div className="flex items-center gap-4">
                                <FormLabel
                                  className={labelClass("end_buffer")}
                                  style={{ width: LABEL_WIDTH }}
                                >
                                  End Buffer
                                  <RequiredMark />
                                </FormLabel>
                                <FormControl>
                                  <Input
                                    id="end-buffer-time-input"
                                    type="number"
                                    inputMode="numeric"
                                    min={0}
                                    step={1}
                                    placeholder="Enter end buffer in minutes"
                                    className={inputClass("end_buffer")}
                                    {...field}
                                  />
                                </FormControl>
                              </div>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {/* Min Time to Start */}
                        <FormField
                          control={form.control}
                          name="min_time_to_start"
                          render={({ field }) => (
                            <FormItem>
                              <div className="flex items-center gap-4">
                                <FormLabel
                                  className={labelClass("min_time_to_start")}
                                  style={{ width: LABEL_WIDTH }}
                                >
                                  Min Time to Start
                                  <RequiredMark />
                                </FormLabel>
                                <FormControl>
                                  <Input
                                    id="min-time-to-start-input"
                                    type="number"
                                    inputMode="numeric"
                                    min={0}
                                    step={1}
                                    placeholder="Enter min time in minutes"
                                    className={inputClass("min_time_to_start")}
                                    {...field}
                                  />
                                </FormControl>
                              </div>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* ── Pricing & Follow-up ───────────────────────────────── */}
                <TabsContent value="pricing" className="mt-4">
                  <Card>
                    <CardContent className="pt-6 pb-5 space-y-5">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-4">
                        {/* Currency (read-only INR) */}
                        <FormItem>
                          <div className="flex items-center gap-4">
                            <FormLabel
                              className="z-label shrink-0 mb-0"
                              style={{ width: LABEL_WIDTH }}
                            >
                              Currency
                            </FormLabel>
                            <Select defaultValue="INR" disabled>
                              <SelectTrigger
                                id="currency-select"
                                className="flex-1 max-w-[400px]"
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="INR">
                                  Indian Rupee (INR)
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </FormItem>

                        {/* Amount */}
                        <FormField
                          control={form.control}
                          name="cost"
                          render={({ field }) => (
                            <FormItem>
                              <div className="flex items-center gap-4">
                                <FormLabel
                                  className={labelClass("cost")}
                                  style={{ width: LABEL_WIDTH }}
                                >
                                  Amount
                                  <RequiredMark />
                                </FormLabel>
                                <FormControl>
                                  <Input
                                    id="service-amount-input"
                                    type="number"
                                    placeholder="Enter amount"
                                    min={0}
                                    step="0.01"
                                    className={cn(
                                      "max-w-[400px]",
                                      inputClass("cost")
                                    )}
                                    {...field}
                                  />
                                </FormControl>
                              </div>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      {/* Follow-up toggle */}
                      <FormField
                        control={form.control}
                        name="is_followup_available"
                        render={({ field }) => (
                          <FormItem>
                            <div className="flex items-center gap-4">
                              <FormLabel
                                className={labelClass("is_followup_available")}
                                style={{ width: LABEL_WIDTH }}
                              >
                                Follow-up Available
                              </FormLabel>
                              <FormControl>
                                <Switch
                                  checked={field.value}
                                  onCheckedChange={field.onChange}
                                />
                              </FormControl>
                            </div>
                          </FormItem>
                        )}
                      />

                      {isFollowupAvailable && (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-4">
                          <FormField
                            control={form.control}
                            name="followup_limit_days"
                            render={({ field }) => (
                              <FormItem>
                                <div className="flex items-center gap-4">
                                  <FormLabel
                                    className={labelClass(
                                      "followup_limit_days"
                                    )}
                                    style={{ width: LABEL_WIDTH }}
                                  >
                                    Follow-up Limit Days
                                    <RequiredMark />
                                  </FormLabel>
                                  <FormControl>
                                    <Input
                                      id="followup-limit-days-input"
                                      type="number"
                                      min={0}
                                      className={inputClass(
                                        "followup_limit_days"
                                      )}
                                      {...field}
                                    />
                                  </FormControl>
                                </div>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name="followup_event_duration"
                            render={({ field }) => (
                              <FormItem>
                                <div className="flex items-center gap-4">
                                  <FormLabel
                                    className={labelClass(
                                      "followup_event_duration"
                                    )}
                                    style={{ width: LABEL_WIDTH }}
                                  >
                                    Follow-up Duration
                                    <RequiredMark />
                                  </FormLabel>
                                  <FormControl>
                                    <Input
                                      id="followup-event-duration-input"
                                      type="number"
                                      min={0}
                                      className={inputClass(
                                        "followup_event_duration"
                                      )}
                                      {...field}
                                    />
                                  </FormControl>
                                </div>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name="followup_cost"
                            render={({ field }) => (
                              <FormItem>
                                <div className="flex items-center gap-4">
                                  <FormLabel
                                    className={labelClass("followup_cost")}
                                    style={{ width: LABEL_WIDTH }}
                                  >
                                    Follow-up Cost
                                    <RequiredMark />
                                  </FormLabel>
                                  <FormControl>
                                    <Input
                                      id="followup-cost-input"
                                      type="number"
                                      min={0}
                                      step="0.01"
                                      className={inputClass("followup_cost")}
                                      {...field}
                                    />
                                  </FormControl>
                                </div>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name="followup_description"
                            render={({ field }) => (
                              <FormItem className="lg:col-span-2">
                                <div className="flex items-start gap-4">
                                  <FormLabel
                                    className={cn(
                                      labelClass("followup_description"),
                                      "pt-2"
                                    )}
                                    style={{ width: LABEL_WIDTH }}
                                  >
                                    Follow-up Description
                                  </FormLabel>
                                  <FormControl>
                                    <Textarea
                                      id="followup-description-input"
                                      rows={3}
                                      className={cn(
                                        "flex-1 resize-none",
                                        isFieldDirty("followup_description") &&
                                          "border-warning border-2"
                                      )}
                                      {...field}
                                    />
                                  </FormControl>
                                </div>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* ── Staff & Visibility ────────────────────────────────── */}
                <TabsContent value="staff" className="mt-4">
                  <Card>
                    <CardContent className="pt-6 pb-5 space-y-5">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-4">
                        <FormField
                          control={form.control}
                          name="is_active"
                          render={({ field }) => (
                            <FormItem>
                              <div className="flex items-center gap-4">
                                <FormLabel
                                  className={labelClass("is_active")}
                                  style={{ width: LABEL_WIDTH }}
                                >
                                  Active
                                </FormLabel>
                                <FormControl>
                                  <Switch
                                    checked={field.value}
                                    onCheckedChange={field.onChange}
                                  />
                                </FormControl>
                              </div>
                              <p className="z-hint text-muted-foreground pl-[186px]">
                                Enables operational booking for this service.
                              </p>
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="visible"
                          render={({ field }) => (
                            <FormItem>
                              <div className="flex items-center gap-4">
                                <FormLabel
                                  className={labelClass("visible")}
                                  style={{ width: LABEL_WIDTH }}
                                >
                                  Visible
                                </FormLabel>
                                <FormControl>
                                  <Switch
                                    checked={field.value}
                                    onCheckedChange={field.onChange}
                                  />
                                </FormControl>
                              </div>
                              <p className="z-hint text-muted-foreground pl-[186px]">
                                Shows this service in patient-facing lists.
                              </p>
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="order"
                          render={({ field }) => (
                            <FormItem>
                              <div className="flex items-center gap-4">
                                <FormLabel
                                  className={labelClass("order")}
                                  style={{ width: LABEL_WIDTH }}
                                >
                                  Order
                                  <RequiredMark />
                                </FormLabel>
                                <FormControl>
                                  <Input
                                    id="service-order-input"
                                    type="number"
                                    min={0}
                                    className={cn(
                                      "max-w-[200px]",
                                      inputClass("order")
                                    )}
                                    {...field}
                                  />
                                </FormControl>
                              </div>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      {/* Staff selection */}
                      <div className="space-y-2 pt-2">
                        <div className="flex items-center justify-between">
                          <p className="z-label">
                            Select Staff
                            {!isEditMode && <RequiredMark />}
                          </p>
                          {selectedStaffIds.length > 0 && (
                            <Badge variant="secondary" className="z-caption">
                              {selectedStaffIds.length} selected
                            </Badge>
                          )}
                        </div>
                        <p className="z-caption text-muted-foreground">
                          Select staff who will be available for this service
                        </p>
                        {staffError && (
                          <p className="z-caption font-medium text-destructive">
                            {staffError}
                          </p>
                        )}

                        {isLoadingStaff ? (
                          <div className="flex items-center justify-center py-8">
                            <Loader2 className="h-5 w-5 animate-spin text-primary" />
                          </div>
                        ) : !staffList || staffList.length === 0 ? (
                          <p className="z-caption text-muted-foreground py-4">
                            No staff members found.
                          </p>
                        ) : (
                          <StaffPicker
                            staffList={staffList}
                            selectedStaffIds={selectedStaffIds}
                            onToggle={handleStaffToggle}
                            isStaffDirty={isStaffDirty}
                          />
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
};

// ─── Public shell (data-fetching wrapper) ─────────────────────────────────────

interface AddEditServiceFormProps {
  serviceId: string; // empty string = create mode
  categories: ServiceCategory[];
  onBack: () => void;
}

export const AddEditServiceForm = ({
  serviceId,
  categories,
  onBack,
}: AddEditServiceFormProps) => {
  const isEditMode = !!serviceId;

  const { data: serviceDetail, isLoading: isLoadingDetail } = useServiceDetail(
    serviceId,
    isEditMode
  );
  const { data: staffList = [], isLoading: isLoadingStaff } =
    useStaffForService();

  if (isEditMode && isLoadingDetail) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-7 w-7 animate-spin text-primary" />
          <p className="z-caption text-muted-foreground">
            Loading service data…
          </p>
        </div>
      </div>
    );
  }

  if (isEditMode && !serviceDetail) return null;

  return (
    <ServiceFormInner
      // Key forces full remount with correct defaultValues whenever the
      // target service changes — eliminates the Radix Select blank-value bug.
      key={isEditMode ? serviceDetail!.id : "new"}
      serviceId={serviceId}
      isEditMode={isEditMode}
      categories={categories}
      staffList={staffList}
      isLoadingStaff={isLoadingStaff}
      initialStaffIds={serviceDetail?.staff_ids ?? []}
      initialImageUrl={serviceDetail?.image ?? ""}
      initialName={serviceDetail?.name ?? ""}
      defaultValues={buildDefaultValues(serviceDetail)}
      onBack={onBack}
    />
  );
};
